import type { Theme } from '@mui/material/styles';
import type { ScheduleHealth } from '../../../types/backend';

type Overall = 'HEALTHY' | 'IDLE' | 'DEGRADED' | 'DOWN' | 'UNKNOWN';

/** Trigger model for a reconciler — mirrors backend `TriggerKind`. */
export type TriggerKind = 'scheduled' | 'event_driven' | 'manual';

// IDLE is calm/neutral — alive with nothing to converge. NEVER red. Rendered as
// a neutral 'default' chip with a grey accent (see overallAccent below).
// UNKNOWN is "we genuinely don't know" — neutral warning-toned, distinct from
// both IDLE's disabled-grey and DEGRADED's solid yellow.
export const OVERALL_COLOR: Record<Overall, 'success' | 'warning' | 'error' | 'default'> = {
  HEALTHY: 'success',
  IDLE: 'default',
  DEGRADED: 'warning',
  DOWN: 'error',
  UNKNOWN: 'default',
};

// Worst-first: DOWN and DEGRADED need attention; UNKNOWN sorts above the resting
// states (IDLE/HEALTHY) because an unknown verdict warrants a look before a
// known-fine one, but below the actively-broken states.
export const OVERALL_ORDER: Record<Overall, number> = {
  DOWN: 0,
  DEGRADED: 1,
  UNKNOWN: 2,
  IDLE: 3,
  HEALTHY: 4,
};

/**
 * Resolve the dot/border accent colour for an overall verdict. The StatusChip
 * colours map cleanly to palette `.main` slots except IDLE (calm neutral grey)
 * and UNKNOWN (a muted warning tone — visibly not-grey, not-solid-yellow).
 */
export function overallAccent(overall: Overall, theme: Theme): string {
  switch (overall) {
    case 'HEALTHY':
      return theme.palette.success.main;
    case 'DEGRADED':
      return theme.palette.warning.main;
    case 'DOWN':
      return theme.palette.error.main;
    case 'UNKNOWN':
      return theme.palette.text.secondary;
    case 'IDLE':
    default:
      return theme.palette.text.disabled;
  }
}

/**
 * Short subtitle for a reconciler's trigger model. "Paused" appears ONLY for a
 * scheduled reconciler whose schedule is genuinely DISABLED — never for
 * event-driven/manual, never for unknown-enabled.
 */
export function triggerLabel(schedule: ScheduleHealth): string {
  switch (schedule.trigger_kind) {
    case 'scheduled':
      if (schedule.enabled === true) return schedule.rate ?? 'Scheduled';
      if (schedule.enabled === false) return 'Paused';
      return 'Schedule unknown';
    case 'event_driven':
      return 'Event-driven';
    case 'manual':
      return 'Manual';
    default:
      return 'Unknown trigger';
  }
}

/**
 * Longer human description of what wakes a reconciler. Event-driven surfaces the
 * backend-supplied `source`; manual is on-demand; scheduled is its rate plus a
 * paused suffix when disabled.
 */
export function triggerDescription(schedule: ScheduleHealth): string {
  switch (schedule.trigger_kind) {
    case 'scheduled': {
      const rate = schedule.rate ?? 'Scheduled';
      return schedule.enabled === false ? `${rate} · paused` : rate;
    }
    case 'event_driven':
      return schedule.source ?? 'Runs on events';
    case 'manual':
      return 'Runs on demand';
    default:
      return 'Unknown trigger';
  }
}

/**
 * True ONLY when a scheduled reconciler's schedule is genuinely disabled. This
 * is the single gate for showing "Paused" or a Pause/Resume affordance —
 * event-driven/manual and unknown-enabled are never paused.
 */
export function isGenuinelyPaused(schedule: ScheduleHealth): boolean {
  return schedule.trigger_kind === 'scheduled' && schedule.enabled === false;
}

export function futureTime(iso: string | undefined): string {
  if (!iso) return '—';
  const diff = new Date(iso).getTime() - Date.now();
  if (diff <= 0) return 'due';
  const mins = Math.floor(diff / 60000);
  const hrs = Math.floor(mins / 60);
  if (hrs > 24) return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  if (hrs > 0) return `in ${hrs}h`;
  if (mins > 0) return `in ${mins}m`;
  return 'now';
}
